import {
  Accessor,
  batch,
  createContext,
  createEffect,
  createMemo,
  createSignal,
  JSX,
  onCleanup,
  ParentProps,
  Setter,
  untrack,
  useContext,
} from 'solid-js';
import { TcProject, TcRegion } from './types/tc-tcps-api-service.types';
import { ConnectProjectSelection } from './types/connect-project.types';
import { useQuery } from '@tanstack/solid-query';
import {
  getAvailableRegionsOptions,
  getProjectDetailsOptions,
  updateProject,
} from '@data-access/connect/tc-tcps/generated';
import { makeMergedPersisted } from '@trimble/solidjs-utils';
import { client as tcTcpsClient } from '@data-access/connect/tc-tcps/generated';
import { getRegionalBaseUrl } from '@data-access/connect/tc-tcps';
import { asError, getLogger } from '@trimble/logging';

export interface ConnectProjectProviderProps {
  connectUrl?: string;
  tcTcpsClient?: typeof tcTcpsClient;
}

export type ConnectProjectProviderComponent = (
  props: ParentProps<ConnectProjectProviderProps>
) => JSX.Element;

export interface ConnectProjectContextState {
  regions: Accessor<TcRegion[] | undefined>;
  selectedRegion: Accessor<TcRegion | undefined>;
  // Undefined until the details match selectedProjectId and selectedLocation.
  selectedProject: Accessor<TcProject | undefined>;
  // Display only: keeps the previous project name while the next one loads.
  readonly selectedProjectDisplayName: Accessor<string | undefined>;
  selectedProjectId: Accessor<string | undefined>;
  selectedProjectExplorerUrl: Accessor<string | undefined>;
  selectedProjectPropertiesLibraryUrl: Accessor<string | undefined>;
  selectedTenantTrn: Accessor<string | undefined>;
  selectedLocation: Accessor<string | undefined>;
  selectProject(selection: ConnectProjectSelection): void;
  // Not atomic; switch projects with selectProject.
  setSelectedLocation: Setter<string | undefined>;
  setSelectedProjectId: Setter<string | undefined>;
  projectDetailsLoading: Accessor<boolean>;
  fetchProjectDetailsError: Accessor<Error | undefined>;
  fetchRegionsError: Accessor<Error | undefined>;
}

export const RESULT_LIMIT = 6;

const ConnectProjectContext = createContext<ConnectProjectContextState>();

export const ConnectProjectProvider: ConnectProjectProviderComponent = (
  props
) => {
  const [selectedLocation, setSelectedLocation] = makeMergedPersisted(
    // eslint-disable-next-line solid/reactivity
    createSignal<string | undefined>(undefined),
    {
      name: 'region', // TODO: Why is the persistence for location 'region'? TODO2: Should this be in an enum?
    }
  );

  const [selectedProjectId, setSelectedProjectId] = makeMergedPersisted(
    // eslint-disable-next-line solid/reactivity
    createSignal<string | undefined>(undefined),
    {
      name: 'projectId',
    }
  );

  // Both values key the project details query, so they must change together.
  const selectProject = (selection: ConnectProjectSelection): void => {
    batch(() => {
      setSelectedLocation(selection.location);
      setSelectedProjectId(selection.projectId);
    });
  };

  // Query Connect for the regions.
  const options = getAvailableRegionsOptions({
    client: tcTcpsClient,
    baseUrl: tcTcpsClient.getConfig().baseUrl,
  });
  const tcRegionsQuery = useQuery(() => {
    return { ...options };
  });

  const tcRegions = createMemo(() => {
    const query = tcRegionsQuery;

    if (query.isSuccess) return query.data;
    return undefined;
  });

  const fetchRegionsError = createMemo(() => {
    if (tcRegionsQuery.isError) return tcRegionsQuery.error;
    return undefined;
  });

  const selectedRegion = createMemo(() => {
    const location = selectedLocation();
    if (location === undefined) return undefined;

    return tcRegions()?.find((r) => r.location === location);
  });

  const abortController = new AbortController();

  const projectDetailsQuery = useQuery(() => {
    const region = selectedRegion();
    const projectId = selectedProjectId();

    return {
      enabled: projectId !== undefined && region !== undefined,
      refetchOnWindowFocus: false,
      ...getProjectDetailsOptions({
        path: {
          projectId: projectId!,
        },
        baseUrl: region ? getRegionalBaseUrl(region) : undefined,
        signal: abortController.signal,
      }),
      placeholderData: (previousData) => previousData,
    };
  });

  onCleanup(() => {
    abortController.abort();
  });

  const fetchProjectDetailsError = createMemo(() => {
    if (abortController.signal.aborted) return undefined;
    if (projectDetailsQuery.isError) return projectDetailsQuery.error;
    return undefined;
  });

  const projectDetailsLoading = createMemo(() => {
    return projectDetailsQuery.isFetching;
  });

  // Placeholder data still holds the previous project while the next one loads.
  const selectedProject = createMemo(() => {
    if (!projectDetailsQuery.isSuccess) return undefined;

    const region = selectedRegion();
    if (region === undefined) return undefined;

    const project = projectDetailsQuery.data;
    if (project.id !== selectedProjectId()) return undefined;
    if (project.location !== region.location) return undefined;

    return project;
  });

  const selectedProjectDisplayName = createMemo(() => {
    if (selectedProjectId() === undefined) return undefined;
    if (selectedLocation() === undefined) return undefined;

    return projectDetailsQuery.data?.name;
  });

  // Update the project last visited on when the project changes.
  createEffect(() => {
    const project = selectedProject();
    if (project === undefined) return;

    const region = untrack(tcRegions)?.find(
      (r) => r.location === project.location
    );
    if (region === undefined) return;

    updateProject({
      body: {
        lastVisitedOn: new Date().toISOString(),
      },
      path: {
        projectId: project.id,
      },
      client: props.tcTcpsClient ?? tcTcpsClient,
      baseUrl: getRegionalBaseUrl(region),
    }).catch((err) => {
      getLogger().error(
        'Error updating project last visited on',
        { project, region },
        asError(err)
      );
    });
  });

  const selectedTenantTrn = createMemo(() => {
    const project = selectedProject();
    if (project === undefined) return undefined;

    return `trn:connect:projects:${project.location}:${project.id}`;
  });

  const selectedProjectExplorerUrl = createMemo(() => {
    if (props.connectUrl === undefined) return undefined;

    const projectId = selectedProjectId();
    if (projectId === undefined) return undefined;

    return `${props.connectUrl}/projects/${projectId}/data/folder/`;
  });

  const selectedProjectPropertiesLibraryUrl = createMemo(() => {
    if (props.connectUrl === undefined) return undefined;

    const projectId = selectedProjectId();
    if (projectId === undefined) return undefined;

    return `${props.connectUrl}/projects/${projectId}/property-set-libraries`;
  });

  return (
    <ConnectProjectContext.Provider
      value={{
        regions: tcRegions,
        selectedRegion,
        selectedProject,
        selectedProjectDisplayName,
        selectedProjectId,
        selectedProjectExplorerUrl,
        selectedProjectPropertiesLibraryUrl,
        selectProject,
        setSelectedProjectId,
        selectedTenantTrn,
        selectedLocation,
        setSelectedLocation,
        projectDetailsLoading,
        fetchProjectDetailsError,
        fetchRegionsError,
      }}
    >
      {props.children}
    </ConnectProjectContext.Provider>
  );
};

export const useConnectProject = (): ConnectProjectContextState => {
  const context = useContext(ConnectProjectContext);
  if (!context) {
    throw new Error(
      'useConnectProject must be used within a ConnectProjectProvider'
    );
  }
  return context;
};
